// src/pages/Security/PermissionManager.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { Alert, Button, Checkbox, Form, Input, Modal, Table, Typography } from 'antd';
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  SectionCard,
  StatusTag,
  TabBar,
  tablePagination,
  useFlatPage
} from '../components/ui';
import { IconPlus, IconRefresh } from '../components/ui/icons';
import notify from "../utils/notify";

const { Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

// Custom hook for debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

// Tab content components for better code organization
const PermissionsTab = React.memo(({ permissions, permissionsLoading, error, permissionColumns, setError }) => {
  return (
    <>
      {error && (
        <Alert
          type="error"
          closable
          showIcon
          title="Error"
          description={error}
          style={{ marginBottom: 12 }}
          onClose={() => setError('')}
        />
      )}
      <SectionCard flush title={<CardTitle count={permissions.length}>Permissions</CardTitle>}>
        <Table
          className="hms-table"
          rowKey="permissionId"
          columns={permissionColumns}
          dataSource={permissions}
          loading={permissionsLoading}
          pagination={tablePagination('permission')}
          scroll={{ x: 800 }}
          locale={{ emptyText: <div className="hms-empty">No permissions found</div> }}
        />
      </SectionCard>
    </>
  );
});

const RolePermissionsTab = React.memo(({
  roles,
  rolesLoading,
  permissions,
  assignLoading,
  selectedRole,
  rolePermissions,
  roleSearch,
  setRoleSearch,
  debouncedRoleSearch,
  handleRoleSelect,
  togglePermission,
  RoleInfo,
  BulkButtons
}) => {
  const filteredRoles = useMemo(() => {
    const q = debouncedRoleSearch.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => (r.name ?? '').toLowerCase().includes(q) || (r.id ?? '').toLowerCase().includes(q));
  }, [roles, debouncedRoleSearch]);

  const rolePermSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);

  const assignmentColumns = useMemo(
    () => [
      {
        title: 'Permission',
        dataIndex: 'name',
        render: (t, r) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{t}</span>
            <span className="hms-cell-sub">{r.category || 'General'}</span>
          </div>
        )
      },
      {
        title: 'Status',
        width: 140,
        render: (_, r) => {
          const on = rolePermSet.has(r.permissionId);
          return <StatusTag status={on ? 'available' : ''}>{on ? 'Assigned' : 'Not assigned'}</StatusTag>;
        }
      },
      {
        title: '',
        width: 70,
        align: 'right',
        render: (_, r) => (
          <Checkbox
            checked={rolePermSet.has(r.permissionId)}
            onChange={(e) => togglePermission(r.permissionId, e.target.checked)}
            disabled={!selectedRole || assignLoading}
          />
        )
      }
    ],
    [assignLoading, rolePermSet, selectedRole, togglePermission]
  );

  const rolesPickerColumns = useMemo(
    () => [
      {
        title: 'Role',
        dataIndex: 'name',
        key: 'name',
        render: (text, r) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{text}</span>
            <span className="hms-cell-sub">{r.id}</span>
          </div>
        )
      }
    ],
    []
  );

  return (
    <div className="hms-split">
      <SectionCard
        flush
        title={<CardTitle count={filteredRoles.length}>Roles</CardTitle>}
        extra={<ListToolbar search={{ value: roleSearch, onChange: setRoleSearch, placeholder: 'Search roles' }} />}
      >
        <Table
          className="hms-table"
          rowKey="id"
          columns={rolesPickerColumns}
          dataSource={filteredRoles}
          loading={rolesLoading}
          pagination={tablePagination('role', { pageSize: 8, showSizeChanger: false, hideOnSinglePage: true })}
          rowClassName={(r) => (selectedRole === r.id ? 'selected-row' : '')}
          onRow={(record) => ({ onClick: () => handleRoleSelect(record.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <div className="hms-empty">No roles found</div> }}
        />
      </SectionCard>

      <div className="hms-stack">
        {RoleInfo}
        {selectedRole && (
          <SectionCard
            flush
            title={<CardTitle count={rolePermissions.length}>Role permissions</CardTitle>}
            extra={<BulkButtons type="role" />}
          >
            <Table
              className="hms-table"
              rowKey="permissionId"
              columns={assignmentColumns}
              dataSource={permissions}
              loading={assignLoading}
              pagination={tablePagination('permission')}
              locale={{ emptyText: <div className="hms-empty">No permissions available</div> }}
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
});

const UserRolesTab = React.memo(({
  users,
  usersLoading,
  roles,
  assignLoading,
  selectedUser,
  userRoles,
  userSearch,
  setUserSearch,
  debouncedUserSearch,
  handleUserSelect,
  toggleUserRole,
  UserInfo,
  BulkButtons
}) => {
  const filteredUsers = useMemo(() => {
    const q = debouncedUserSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q)
    );
  }, [users, debouncedUserSearch]);

  const userRoleSet = useMemo(() => new Set(userRoles), [userRoles]);

  const userRoleColumns = useMemo(
    () => [
      { title: 'Role', dataIndex: 'name', render: (t) => <span className="hms-cell-main">{t}</span> },
      {
        title: 'Status',
        width: 140,
        render: (_, r) => {
          const on = userRoleSet.has(r.id);
          return <StatusTag status={on ? 'available' : ''}>{on ? 'Assigned' : 'Not assigned'}</StatusTag>;
        }
      },
      {
        title: '',
        width: 70,
        align: 'right',
        render: (_, r) => (
          <Checkbox
            checked={userRoleSet.has(r.id)}
            onChange={(e) => toggleUserRole(r.id, e.target.checked)}
            disabled={!selectedUser || assignLoading}
          />
        )
      }
    ],
    [assignLoading, selectedUser, toggleUserRole, userRoleSet]
  );

  const usersPickerColumns = useMemo(
    () => [
      {
        title: 'User',
        key: 'name',
        render: (_, u) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—'}</span>
            <span className="hms-cell-sub">{u.email || '—'}</span>
          </div>
        )
      }
    ],
    []
  );

  return (
    <div className="hms-split">
      <SectionCard
        flush
        title={<CardTitle count={filteredUsers.length}>Users</CardTitle>}
        extra={<ListToolbar search={{ value: userSearch, onChange: setUserSearch, placeholder: 'Search users' }} />}
      >
        <Table
          className="hms-table"
          rowKey="id"
          columns={usersPickerColumns}
          dataSource={filteredUsers}
          loading={usersLoading}
          pagination={tablePagination('user', { pageSize: 8, showSizeChanger: false, hideOnSinglePage: true })}
          rowClassName={(u) => (selectedUser === u.id ? 'selected-row' : '')}
          onRow={(record) => ({ onClick: () => handleUserSelect(record.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <div className="hms-empty">No users found</div> }}
        />
      </SectionCard>

      <div className="hms-stack">
        {UserInfo}
        {selectedUser && (
          <SectionCard
            flush
            title={<CardTitle count={userRoles.length}>User roles</CardTitle>}
            extra={<BulkButtons type="user" />}
          >
            <Table
              className="hms-table"
              rowKey="id"
              columns={userRoleColumns}
              dataSource={roles}
              loading={assignLoading}
              pagination={tablePagination('role')}
              locale={{ emptyText: <div className="hms-empty">No roles available</div> }}
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
});

const PermissionManager = () => {
  useFlatPage();
  const { authData } = useAuth();

  // ─── state ─────────────────────────────────────────────
  const [activeKey, setActiveKey] = useState('1');

  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [categories, setCategories] = useState([]);

  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Separate loading states for each tab
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const permissionsLoadingRef = useRef(false); // the timeout below must see the CURRENT value, not the one from when it was set
  permissionsLoadingRef.current = permissionsLoading;
  const [rolesLoading, setRolesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  const [error, setError] = useState('');

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState(new Set(['1'])); // Tab 1 loads initially

  // local search state for pickers with debounce
  const [roleSearch, setRoleSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const debouncedRoleSearch = useDebounce(roleSearch, 300);
  const debouncedUserSearch = useDebounce(userSearch, 300);

  const [form] = Form.useForm();
  const mountedRef = useRef(true);

  // ─── lifecycle: mount/unmount guard ─────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── enhanced data loaders with timeouts ───────────────
  const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await axiosInstance.get(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const fetchPermissions = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setPermissionsLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/RolePermission/Permissions`, {
        params: { hotelId: authData.hotelId },
        signal,
      });
      
      if (!mountedRef.current) return;
      setPermissions(Array.isArray(res.data) ? res.data : []);
      setCategories([...new Set((res.data || []).map((p) => p.category ?? 'General'))]);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load permissions';
      setError(errorMsg);
      notify.error({ 
        title: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setPermissionsLoading(false);
    }
  }, [authData?.hotelId]);

  const fetchRoles = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setRolesLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/Roles/ByHotel/${authData.hotelId}`, { signal });
      if (!mountedRef.current) return;
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load roles';
      setError(errorMsg);
      notify.error({ 
        title: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setRolesLoading(false);
    }
  }, [authData?.hotelId]);

  const fetchUsers = useCallback(async (signal) => {
    if (!authData?.hotelId) return;
    
    setUsersLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`/User`, {
        params: { hotelId: authData.hotelId },
        signal,
      });
      if (!mountedRef.current) return;
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!mountedRef.current || err.name === 'CanceledError' || err.name === 'AbortError') return;
      
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load users';
      setError(errorMsg);
      notify.error({ 
        title: 'Load Error', 
        description: errorMsg,
        duration: 4,
      });
    } finally {
      if (mountedRef.current) setUsersLoading(false);
    }
  }, [authData?.hotelId]);

  // ─── initial: only permissions (faster first paint) ────
  useEffect(() => {
    if (!authData?.hotelId) return;
    
    const controller = new AbortController();
    const loadTimeout = setTimeout(() => {
      if (permissionsLoadingRef.current) {
        controller.abort();
        setPermissionsLoading(false);
        setError('Permissions load timeout. Please try again.');
      }
    }, 15000);

    fetchPermissions(controller.signal);

    return () => {
      controller.abort();
      clearTimeout(loadTimeout);
    };
  }, [authData?.hotelId, fetchPermissions]);

  // ─── lazy load by tab ───────────────────────────────────
  const handleTabChange = (key) => {
    setActiveKey(key);
    
    // If tab hasn't been loaded yet, load its data
    if (!loadedTabs.has(key)) {
      const controller = new AbortController();
      
      switch (key) {
        case '2': // Role-Permissions tab
          fetchRoles(controller.signal);
          break;
        case '3': // User-Roles tab
          fetchUsers(controller.signal);
          break;
        default:
          break;
      }
      
      // Mark tab as loaded
      setLoadedTabs(prev => new Set(prev).add(key));
      
      // Cleanup on tab change if needed
      return () => controller.abort();
    }
  };

  // ─── Permission CRUD ───────────────────────────────────
  const handleCreatePermission = useCallback(async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      const payload = { ...values, hotelId: authData?.hotelId };
      const { data } = await axiosInstance.post('/RolePermission/Permissions', payload);
      
      setPermissions((prev) => [...prev, data]);
      if (!categories.includes(payload.category)) {
        setCategories((prev) => [...prev, payload.category]);
      }
      
      form.resetFields();
      setModalVisible(false);
      notify.success({ 
        title: 'Success', 
        description: 'Permission created successfully',
        duration: 3,
      });
    } catch (err) {
      if (err?.errorFields) return; // form validation error
      
      const errorMsg = err.response?.data?.message || 'Failed to create permission';
      setError(errorMsg);
      notify.error({ 
        title: 'Create Error', 
        description: errorMsg,
      });
    } finally {
      setModalLoading(false);
    }
  }, [authData?.hotelId, categories, form]);

  const handleUpdatePermission = useCallback(async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      const { data } = await axiosInstance.put(`/RolePermission/Permissions/${editingId}`, values);
      
      setPermissions((prev) => prev.map((p) => (p.permissionId === editingId ? data : p)));
      setEditingId(null);
      setModalVisible(false);
      form.resetFields();
      
      notify.success({ 
        title: 'Success', 
        description: 'Permission updated successfully',
        duration: 3,
      });
    } catch (err) {
      if (err?.errorFields) return;
      
      const errorMsg = err.response?.data?.message || 'Failed to update permission';
      setError(errorMsg);
      notify.error({ 
        title: 'Update Error', 
        description: errorMsg,
      });
    } finally {
      setModalLoading(false);
    }
  }, [editingId, form]);

  const handleDeletePermission = useCallback((permissionId) => {
    confirm({
      title: 'Delete Permission',
      content: 'Are you sure you want to delete this permission? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      maskClosable: true,
      onOk: async () => {
        try {
          await axiosInstance.delete(`/RolePermission/Permissions/${permissionId}`);
          setPermissions((prev) => prev.filter((p) => p.permissionId !== permissionId));
          setRolePermissions((prev) => prev.filter((id) => id !== permissionId));
          
          notify.success({ 
            title: 'Success', 
            description: 'Permission deleted successfully',
            duration: 3,
          });
        } catch (err) {
          const errorMsg = err.response?.data?.message || 'Failed to delete permission';
          setError(errorMsg);
          notify.error({ 
            title: 'Delete Error', 
            description: errorMsg,
          });
        }
      },
    });
  }, []);

  // ─── Role-permission assignment ─────────────────────────
  const handleRoleSelect = useCallback(async (roleId) => {
    try {
      setAssignLoading(true);
      if (!roleId) {
        setSelectedRole(null);
        setRolePermissions([]);
        return;
      }
      
      const { data } = await fetchWithTimeout(`/RolePermission/Roles/${roleId}/Permissions`);
      setSelectedRole(roleId);
      setRolePermissions((data || []).map((p) => p.permissionId));
    } catch (err) {
      notify.error({ 
        title: 'Load Error', 
        description: 'Failed to load role permissions',
      });
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const togglePermission = useCallback(
    async (permissionId, checked) => {
      if (!selectedRole) {
        notify.warning({ 
          title: 'Warning', 
          description: 'Please select a role first',
        });
        return;
      }
      
      try {
        setAssignLoading(true);
        if (checked) {
          await axiosInstance.post(`/RolePermission/Roles/${selectedRole}/Permissions`, {
            permissionIds: [permissionId],
          });
          setRolePermissions((prev) => (prev.includes(permissionId) ? prev : [...prev, permissionId]));
        } else {
          await axiosInstance.delete(`/RolePermission/Roles/${selectedRole}/Permissions/${permissionId}`);
          setRolePermissions((prev) => prev.filter((id) => id !== permissionId));
        }
      } catch (err) {
        notify.error({
          title: 'Update Error',
          description: 'Failed to update role permissions',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedRole],
  );

  const bulkAssignPermissions = useCallback(
    async (ids) => {
      if (!selectedRole) return;
      
      try {
        setAssignLoading(true);
        await axiosInstance.post(`/RolePermission/Roles/${selectedRole}/Permissions`, { 
          permissionIds: ids 
        });
        setRolePermissions(ids);
        
        notify.success({
          title: 'Success',
          description: 'All permissions assigned successfully',
        });
      } catch (err) {
        notify.error({
          title: 'Bulk Assign Error',
          description: 'Failed to assign permissions',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedRole],
  );

  // ─── User-role assignment ──────────────────────────────
  const handleUserSelect = useCallback(async (userId) => {
    try {
      setAssignLoading(true);
      if (!userId) {
        setSelectedUser(null);
        setUserRoles([]);
        return;
      }
      
      const { data } = await fetchWithTimeout(`/User/${userId}/Roles`);
      setSelectedUser(userId);
      setUserRoles((data?.roles || []).map((r) => r.id));
    } catch (err) {
      notify.error({
        title: 'Load Error',
        description: err.response?.data?.message || err.message || 'Failed to load user roles',
      });
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const toggleUserRole = useCallback(
    async (roleId, checked) => {
      if (!selectedUser) {
        notify.warning({ 
          title: 'Warning', 
          description: 'Please select a user first' 
        });
        return;
      }
      
      try {
        setAssignLoading(true);
        if (checked) {
          await axiosInstance.post(`/User/${selectedUser}/Roles`, { roleIds: [roleId] });
          setUserRoles((prev) => (prev.includes(roleId) ? prev : [...prev, roleId]));
        } else {
          await axiosInstance.delete(`/User/${selectedUser}/Roles/${roleId}`);
          setUserRoles((prev) => prev.filter((id) => id !== roleId));
        }
      } catch (err) {
        notify.error({
          title: 'Update Error',
          description: err.response?.data?.message || err.message || 'Failed to update user role',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedUser],
  );

  const bulkAssignRoles = useCallback(
    async (ids) => {
      if (!selectedUser) return;
      
      try {
        setAssignLoading(true);
        await axiosInstance.post(`/User/${selectedUser}/Roles`, { roleIds: ids });
        setUserRoles(ids);
        
        notify.success({
          title: 'Success',
          description: 'All roles assigned successfully',
        });
      } catch (err) {
        notify.error({
          title: 'Bulk Assign Error',
          description: err.response?.data?.message || err.message || 'Failed to bulk assign roles',
        });
      } finally {
        setAssignLoading(false);
      }
    },
    [selectedUser],
  );

  // ─── column definitions (memoized) ─────────────────────
  const permissionColumns = useMemo(
    () => [
      { title: 'Name', dataIndex: 'name', render: (text) => <span className="hms-cell-main">{text}</span> },
      {
        title: 'Description',
        dataIndex: 'description',
        render: (t) => t || <span className="hms-muted">—</span>
      },
      {
        title: 'Category',
        dataIndex: 'category',
        width: 160,
        render: (c) => (c ? <StatusTag status="confirmed">{c}</StatusTag> : <span className="hms-muted">—</span>)
      },
      {
        title: '',
        width: 100,
        fixed: 'right',
        align: 'right',
        render: (_, record) => (
          <RowActions
            actions={[
              {
                key: 'edit',
                label: 'Edit permission',
                icon: 'edit',
                onClick: () => {
                  setEditingId(record.permissionId);
                  form.setFieldsValue(record);
                  setModalVisible(true);
                }
              },
              {
                key: 'delete',
                label: 'Delete permission',
                icon: 'delete',
                danger: true,
                onClick: () => handleDeletePermission(record.permissionId)
              }
            ]}
          />
        )
      }
    ],
    [form, handleDeletePermission]
  );

  // ─── helper components (memo) ──────────────────────────
  const RoleInfo = useMemo(() => {
    if (!selectedRole) {
      return (
        <SectionCard>
          <div className="hms-empty">Select a role on the left to manage its permissions</div>
        </SectionCard>
      );
    }

    const role = roles.find((x) => x.id === selectedRole);
    if (!role) return null;

    return (
      <SectionCard title="Selected role">
        <DetailList
          items={[
            { label: 'Name', value: role.name },
            { label: 'ID', value: role.id },
            { label: 'Permissions', value: rolePermissions.length }
          ]}
        />
      </SectionCard>
    );
  }, [rolePermissions.length, roles, selectedRole]);

  const UserInfo = useMemo(() => {
    if (!selectedUser) {
      return (
        <SectionCard>
          <div className="hms-empty">Select a user on the left to manage their roles</div>
        </SectionCard>
      );
    }

    const u = users.find((x) => x.id === selectedUser);
    if (!u) return null;

    return (
      <SectionCard title="Selected user">
        <DetailList
          items={[
            { label: 'Name', value: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() },
            { label: 'Email', value: u.email },
            { label: 'Roles', value: userRoles.length }
          ]}
        />
      </SectionCard>
    );
  }, [selectedUser, userRoles.length, users]);

  const BulkButtons = useCallback(
    ({ type }) => {
      const ready = (type === 'role' && selectedRole) || (type === 'user' && selectedUser);
      if (!ready) return null;

      const ids = type === 'role' ? permissions.map((p) => p.permissionId) : roles.map((r) => r.id);
      const handler = type === 'role' ? bulkAssignPermissions : bulkAssignRoles;
      const title = type === 'role' ? 'permissions' : 'roles';

      return (
        <div className="hms-form-actions" style={{ marginTop: 0 }}>
          <Button
            type="primary"
            onClick={() =>
              confirm({
                title: `Assign all ${title}?`,
                content: `This will assign all ${ids.length} ${title} to the selected ${type}.`,
                centered: true,
                onOk: () => handler(ids)
              })
            }
          >
            Assign all
          </Button>
          <Button
            danger
            onClick={() =>
              confirm({
                title: `Remove all ${title}?`,
                content: `This will remove all ${title} from the selected ${type}.`,
                centered: true,
                onOk: async () => {
                  setAssignLoading(true);
                  try {
                    if (type === 'role') {
                      await axiosInstance.delete(`/RolePermission/Roles/${selectedRole}/Permissions`);
                      setRolePermissions([]);
                    } else {
                      await axiosInstance.delete(`/User/${selectedUser}/Roles`);
                      setUserRoles([]);
                    }
                    notify.success({
                      title: 'Success',
                      description: `All ${title} removed successfully`
                    });
                  } catch (err) {
                    notify.error({
                      title: 'Error',
                      description: `Failed to remove ${title}`
                    });
                  } finally {
                    setAssignLoading(false);
                  }
                }
              })
            }
          >
            Remove all
          </Button>
        </div>
      );
    },
    [bulkAssignPermissions, bulkAssignRoles, permissions, roles, selectedRole, selectedUser]
  );

  // Get current loading state based on active tab
  const getCurrentLoading = () => {
    switch (activeKey) {
      case '1': return permissionsLoading;
      case '2': return rolesLoading;
      case '3': return usersLoading;
      default: return false;
    }
  };

  // Get refresh function for current tab
  const getRefreshFunction = () => {
    switch (activeKey) {
      case '1': return fetchPermissions;
      case '2': return fetchRoles;
      case '3': return fetchUsers;
      default: return () => {};
    }
  };

  // ─── render ────────────────────────────────
  if (!authData?.hotelId) {
    return <Alert title="Authentication required" description="Please log in to access permission management." type="warning" showIcon />;
  }

  const openGuide = () =>
    Modal.info({
      title: 'Permission management guide',
      width: 520,
      centered: true,
      content: (
        <div>
          <p>A three-step process:</p>
          <ol style={{ paddingLeft: 20 }}>
            <li>Define permissions: create and manage individual permissions.</li>
            <li>Assign to roles: group permissions into roles.</li>
            <li>Assign roles to users: grant roles to people.</li>
          </ol>
          <p className="hms-muted">Tip: use the Assign all and Remove all buttons for faster set-up.</p>
        </div>
      )
    });

  return (
    <div className="hms-page pm-page">
      <PageHeader
        actions={
          <>
            <Button
              icon={<IconRefresh />}
              loading={getCurrentLoading()}
              onClick={() => {
                const controller = new AbortController();
                getRefreshFunction()(controller.signal);
              }}
            >
              Refresh
            </Button>
            <Button onClick={openGuide}>Guide</Button>
            <Button
              type="primary"
              icon={<IconPlus />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Add permission
            </Button>
          </>
        }
      />

      <SectionCard flush>
        <TabBar
          tabs={[
            { key: '1', label: 'Permissions', count: permissions.length },
            { key: '2', label: 'Role permissions', count: roles.length },
            { key: '3', label: 'User roles', count: users.length }
          ]}
          value={activeKey}
          onChange={handleTabChange}
        />
      </SectionCard>

      <div style={{ marginTop: 16 }}>
        {activeKey === '1' && (
          <PermissionsTab
            permissions={permissions}
            permissionsLoading={permissionsLoading}
            error={error}
            permissionColumns={permissionColumns}
            setError={setError}
          />
        )}

        {activeKey === '2' && (
          <RolePermissionsTab
            roles={roles}
            rolesLoading={rolesLoading}
            permissions={permissions}
            assignLoading={assignLoading}
            selectedRole={selectedRole}
            rolePermissions={rolePermissions}
            roleSearch={roleSearch}
            setRoleSearch={setRoleSearch}
            debouncedRoleSearch={debouncedRoleSearch}
            handleRoleSelect={handleRoleSelect}
            togglePermission={togglePermission}
            RoleInfo={RoleInfo}
            BulkButtons={BulkButtons}
          />
        )}

        {activeKey === '3' && (
          <UserRolesTab
            users={users}
            usersLoading={usersLoading}
            roles={roles}
            assignLoading={assignLoading}
            selectedUser={selectedUser}
            userRoles={userRoles}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            debouncedUserSearch={debouncedUserSearch}
            handleUserSelect={handleUserSelect}
            toggleUserRole={toggleUserRole}
            UserInfo={UserInfo}
            BulkButtons={BulkButtons}
          />
        )}
      </div>

      {/* create / edit permission */}
      <Modal
        open={modalVisible}
        title={editingId ? 'Edit permission' : 'Create permission'}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
          setEditingId(null);
        }}
        onOk={editingId ? handleUpdatePermission : handleCreatePermission}
        confirmLoading={modalLoading}
        destroyOnHidden
        okText={editingId ? 'Save changes' : 'Create permission'}
        width={500}
      >
        <Form layout="vertical" form={form} preserve={false}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Name is required' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="e.g. MaintenanceAccess" maxLength={50} />
          </Form.Item>
          <Form.Item label="Description" name="description" rules={[{ max: 200, message: 'Description cannot exceed 200 characters' }]}>
            <TextArea rows={3} placeholder="What this permission allows" maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[
              { required: true, message: 'Category is required' },
              { min: 2, message: 'Category must be at least 2 characters' }
            ]}
          >
            <Input list="categories" placeholder="e.g. Maintenance, Reports, Settings" maxLength={30} />
          </Form.Item>
          <datalist id="categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionManager;
